import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { FiHome, FiCreditCard, FiUsers, FiSettings } from 'react-icons/fi';

import { useAuth } from '@/hooks/useAuth.jsx';
import { useHasRole } from '@/hooks/useHasRole';
import { db } from '@/services/supabase';
import AdminDashboard from '@/components/admin/AdminDashboard';
import PaymentManager from '@/components/admin/PaymentManager';
import CustomerTable from '@/components/admin/CustomerTable';
import SettingsForm from '@/components/admin/SettingsForm';
import { AppSidebar } from '@/components/app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const { user, logout } = useAuth();
  const { hasRole, isLoading } = useHasRole(['admin', 'organizator']);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 0 }),
    []
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    []
  );

  const { data: eventSettings } = useQuery({
    queryKey: ['event-settings'],
    queryFn: db.eventSettings.getLatest,
    staleTime: 5 * 60 * 1000,
  });

  const { data: orderStats } = useQuery({
    queryKey: ['admin-stats-sidebar'],
    queryFn: async () => {
      const stats = await db.orders.getStats();
      const statsObj = Array.isArray(stats) ? stats[0] || {} : stats || {};
      return {
        totalOrders: Number(statsObj.total_orders ?? 0),
        paidOrders: Number(statsObj.paid_orders ?? 0),
        pendingOrders: Number(statsObj.pending_orders ?? 0),
        totalAmount: Number(statsObj.total_amount ?? 0),
        soldTickets: Number(statsObj.sold_tickets ?? 0),
      };
    },
    staleTime: 30 * 1000,
  });

  const { data: ticketStats } = useQuery({
    queryKey: ['ticket-stats-sidebar'],
    queryFn: db.analytics.getTicketStats,
    staleTime: 30 * 1000,
  });

  const { data: dailyStats } = useQuery({
    queryKey: ['daily-stats-sidebar'],
    queryFn: () => db.analytics.getDailyStats(7),
    staleTime: 60 * 1000,
  });

  const formattedDeadline = eventSettings?.sales_end
    ? new Date(eventSettings.sales_end).toLocaleDateString('sk')
    : '—';
  const formattedPrice = eventSettings?.ticket_price
    ? currencyFormatter.format(Number(eventSettings.ticket_price))
    : '—';

  const isSalesActive = useMemo(() => {
    if (!eventSettings?.sales_start || !eventSettings?.sales_end) {
      return false;
    }
    const now = new Date();
    return now >= new Date(eventSettings.sales_start) && now <= new Date(eventSettings.sales_end);
  }, [eventSettings]);

  const latestDaily = Array.isArray(dailyStats) && dailyStats.length > 0 ? dailyStats[0] : null;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-200">
        Načítavam oprávnenia...
      </div>
    );
  }

  if (!hasRole) {
    return (
      <div className="text-center text-error-400 mt-10">
        Nemáš oprávnenie na prístup k tejto stránke.
      </div>
    );
  }

  const tabs = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: FiHome,
      component: AdminDashboard,
    },
    {
      id: 'payments',
      label: 'Platby',
      icon: FiCreditCard,
      component: PaymentManager,
    },
    {
      id: 'customers',
      label: 'Zákazníci',
      icon: FiUsers,
      component: CustomerTable,
    },
    {
      id: 'settings',
      label: 'Nastavenia',
      icon: FiSettings,
      component: SettingsForm,
    },
  ];

  const activeComponent = tabs.find((tab) => tab.id === activeTab)?.component;
  const ActiveComponent = activeComponent || (() => <div>Tab not found</div>);

  const handleLogout = () => {
    if (window.confirm('Naozaj sa chcete odhlásiť?')) {
      logout();
    }
  };

  const quickStats = [
    {
      label: 'Status',
      value: isSalesActive ? 'Predaj aktívny' : 'Predaj pozastavený',
      valueClass: isSalesActive ? 'text-success-200' : 'text-warning-200',
    },
    {
      label: 'Deadline',
      value: formattedDeadline,
    },
    {
      label: 'Cena lístka',
      value: formattedPrice,
    },
  ];

  const dataSnapshot = [
    {
      label: 'Objednávky celkom',
      value: numberFormatter.format(orderStats?.totalOrders ?? 0),
    },
    {
      label: 'Čakajúce / zaplatené',
      value: `${numberFormatter.format(orderStats?.pendingOrders ?? 0)} / ${numberFormatter.format(orderStats?.paidOrders ?? 0)}`,
    },
    {
      label: 'Predané lístky',
      value: numberFormatter.format(orderStats?.soldTickets ?? 0),
    },
    {
      label: 'Dostupné lístky',
      value: numberFormatter.format(ticketStats?.available_tickets ?? 0),
    },
    {
      label: 'Celkový príjem',
      value: currencyFormatter.format(orderStats?.totalAmount ?? 0),
    },
    {
      label: 'Posledná aktivita',
      value: latestDaily?.date ? new Date(latestDaily.date).toLocaleDateString('sk') : '—',
    },
  ];

  const quickActions = [
    {
      label: 'Správa platieb',
      icon: 'money',
      onClick: () => setActiveTab('payments'),
    },
    {
      label: 'Spustiť žrebovanie',
      icon: 'bullseye',
      onClick: () => window.open('/draw', '_blank', 'noopener'),
    },
    {
      label: 'Export dát',
      icon: 'report',
      onClick: () => {
        const data = `Tombola Export - ${new Date().toLocaleDateString('sk')}`;
        const blob = new Blob([data], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'tombola-export.txt';
        anchor.click();
        URL.revokeObjectURL(url);
      },
    },
  ];

  const activeTabLabel = tabs.find((tab) => tab.id === activeTab)?.label ?? 'Sekcia';

  return (
    <SidebarProvider>
      <AppSidebar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        quickStats={quickStats}
        dataSnapshot={dataSnapshot}
        quickActions={quickActions}
        userEmail={user?.email}
        onLogout={handleLogout}
      />
      <SidebarInset className="flex min-h-screen flex-1 flex-col bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-slate-100 md:m-4 md:rounded-xl md:border md:border-slate-800/60 md:shadow-lg overflow-hidden">
        <header className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-slate-800/60 bg-slate-950/80 backdrop-blur md:rounded-t-xl">
          <div className="flex items-center gap-2 px-4 md:px-8">
            <SidebarTrigger className="-ml-1 md:hidden" aria-label="Zobraziť navigáciu" />
            <Separator orientation="vertical" className="mr-2 hidden h-4 md:block" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink href="#">Administrácia</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <BreadcrumbPage>{activeTabLabel}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0 md:p-8 md:pt-4">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="min-h-[60vh]"
          >
            <ActiveComponent />
          </motion.div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default AdminPage;